import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ChevronRight, ShieldCheck, Zap, Loader2 } from "lucide-react";
import { api } from "../services/api";

interface Variant {
  id: number;
  ram: string;
  storage: string;
  price: number;
}

interface DeviceDetail {
  id: number;
  name: string;
  image: string | null;
  variants: Variant[];
}

const DeviceDetails: React.FC = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const modelNameFromURL = searchParams.get("model") || "Device";

  const [device, setDevice] = useState<DeviceDetail | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [loading, setLoading] = useState(true);

  // --- REUSABLE GB TO TB CONVERSION UTILITY (CRASH SAFEPUSH) ---
  const formatStorage = (storageValue: any): string => {
    // API data string ho ya number, safe text conversion handle karega
    const storageStr = String(storageValue || "");
    
    const numericValue = parseInt(storageStr.replace(/\D/g, ""), 10);
    if (isNaN(numericValue)) return storageStr;

    if (numericValue >= 1024) {
      const tbValue = numericValue / 1024;
      return `${tbValue} TB`;
    }
    return `${numericValue} GB`;
  };

  // --- CORE LOGIC (UNTOUCHED) ---
  useEffect(() => {
    if (deviceId) {
      setLoading(true);
      api.get(`/device/${deviceId}`)
        .then((data) => {
          setDevice(data);
          if (data?.variants?.length > 0) {
            setSelectedVariant(data.variants[0]);
          }
        })
        .catch(() => console.error("Failed to load device"))
        .finally(() => setLoading(false));
    }
  }, [deviceId]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-10 h-10 text-[#363C90] animate-spin" />
        <p className="text-slate-400 text-sm font-bold uppercase tracking-widest text-center">
          Fetching details for <br/> <span className="text-slate-600">{modelNameFromURL}</span>
        </p>
      </div>
    );
  }

  if (!device) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 pt-6">
        
        {/* --- BACK BUTTON (SHARP) --- */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-slate-400 hover:text-[#363C90] transition-colors mb-6 text-[11px] font-bold uppercase tracking-widest"
        >
          <ArrowLeft size={14} /> Change Model
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* --- LEFT: DEVICE IMAGE STAGE --- */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="bg-white rounded-3xl p-8 md:p-12 flex items-center justify-center relative border border-slate-100 shadow-sm aspect-square overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/20 to-transparent" />
              
              {device.image && (
                <img 
                  src={device.image} 
                  alt={device.name} 
                  className="relative z-10 h-full w-full object-contain drop-shadow-xl transition-transform duration-700 hover:scale-105"
                />
              )}
            </div>
            
            {/* Quick Trust Badges (Compact) */}
            <div className="grid grid-cols-3 gap-3 mt-6">
               {[
                 { icon: Zap, label: "Instant Quote", color: "text-indigo-500" },
                 { icon: ShieldCheck, label: "Secure Data", color: "text-emerald-500" },
                 { icon: CheckCircle2, label: "Free Pickup", color: "text-orange-400" }
               ].map((badge, i) => (
                 <div key={i} className="flex flex-col items-center text-center p-3 bg-white border border-slate-100 rounded-2xl shadow-sm">
                    <badge.icon size={18} className={`${badge.color} mb-1.5`} />
                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-tight leading-none">{badge.label}</span>
                 </div>
               ))}
            </div>
          </div>

          {/* --- RIGHT: SELECTION AREA --- */}
          <div className="lg:col-span-7">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-wider rounded-md border border-emerald-100">
                  Step 2
                </span>
                <span className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Select Variant</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-3 tracking-tight">
                {device.name}
              </h1>
              <p className="text-slate-400 text-sm font-medium leading-relaxed max-w-xl">
                Choose the correct RAM and Storage variant. A wrong selection might lead to a price change during physical inspection.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                 <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
                  Storage Variants
                </h3>
                <div className="h-px w-full bg-slate-100" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {device.variants.map((v) => {
                  const isActive = selectedVariant?.id === v.id;
                  return (
                    <div
                      key={v.id}
                      onClick={() => setSelectedVariant(v)}
                      className={`
                        cursor-pointer p-4 rounded-2xl border transition-all duration-200 relative
                        ${isActive 
                          ? "border-[#363C90] bg-indigo-50/30 shadow-md shadow-indigo-100/50" 
                          : "border-slate-100 bg-white hover:border-slate-200"}
                      `}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-baseline gap-2">
                          <p className={`text-lg font-black ${isActive ? "text-[#363C90]" : "text-slate-800"}`}>
                            {formatStorage(v.storage)}
                          </p>
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                            / {v.ram} RAM
                          </span>
                        </div>
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-all ${isActive ? "border-[#363C90] bg-[#363C90]" : "border-slate-200"}`}>
                           {isActive && <CheckCircle2 size={12} className="text-white" />}
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t border-slate-50 flex items-center justify-between">
                         <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Estimated Price</span>
                         <span className={`text-lg font-black ${isActive ? "text-[#363C90]" : "text-slate-700"}`}>
                           ₹{v.price.toLocaleString()}
                         </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* --- COMPACT FLOATING CTA --- */}
            {selectedVariant && (
              <div className="mt-8 bg-slate-900 rounded-2xl p-6 flex items-center justify-between gap-4 shadow-xl shadow-slate-900/10 border border-slate-800 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="min-w-0">
                  <p className="text-slate-400 text-[10px] font-black uppercase tracking-[2px] mb-1">Selling Price</p>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-white text-3xl font-black italic tracking-tight">₹{selectedVariant.price.toLocaleString()}</span>
                    <span className="text-emerald-400 text-[10px] font-bold uppercase tracking-tighter">*Upto</span>
                  </div>
                </div>

                <button
                  onClick={() => navigate(`/sell/condition/${device.id}/${selectedVariant.id}`)}
                  className="bg-emerald-500 hover:bg-emerald-400 text-white px-6 py-3.5 rounded-xl font-black text-sm flex items-center gap-2 transition-all hover:translate-x-1 active:scale-95 shadow-lg shadow-emerald-500/20"
                >
                  CONTINUE <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceDetails;